import { Autocomplete, Box, Chip, Stack, TextField, Typography } from '@mui/material';

export const SERVICE_SPECIALIZATION_OPTIONS = [
    { code: 'custom_software_development', label: 'Разработка ПО на заказ', group: 'Разработка' },
    { code: 'website_development', label: 'Разработка сайтов', group: 'Разработка' },
    { code: 'web_application_development', label: 'Веб-приложения', group: 'Разработка' },
    { code: 'mobile_application_development', label: 'Мобильные приложения', group: 'Разработка' },
    { code: 'telegram_bot_development', label: 'Telegram-боты', group: 'Разработка' },
    { code: 'telegram_mini_app_development', label: 'Telegram Mini Apps', group: 'Разработка' },
    { code: 'software_code_changes', label: 'Доработка кода', group: 'Разработка' },
    { code: 'crm_implementation', label: 'Внедрение CRM', group: 'CRM и 1С' },
    { code: 'crm_configuration', label: 'Настройка CRM', group: 'CRM и 1С' },
    { code: 'crm_integration', label: 'Интеграция CRM', group: 'CRM и 1С' },
    { code: 'one_c_configuration', label: 'Настройка 1С', group: 'CRM и 1С' },
    { code: 'one_c_integration', label: 'Интеграция 1С', group: 'CRM и 1С' },
    { code: 'graphic_design', label: 'Графический дизайн', group: 'Дизайн' },
    { code: 'packaging_design', label: 'Дизайн упаковки', group: 'Дизайн' },
    { code: 'marketplace_card_design', label: 'Дизайн карточек', group: 'Дизайн' },
    { code: 'marketplace_operations', label: 'Ведение маркетплейсов', group: 'Маркетплейсы' },
    { code: 'marketplace_growth', label: 'Продвижение на маркетплейсах', group: 'Маркетплейсы' },
    { code: 'loading_services', label: 'Услуги грузчиков', group: 'Переезды и перевозки' },
    { code: 'rigging_services', label: 'Такелажные работы', group: 'Переезды и перевозки' },
    { code: 'cargo_transportation', label: 'Грузоперевозки', group: 'Переезды и перевозки' },
    { code: 'combined_move_service', label: 'Переезд под ключ', group: 'Переезды и перевозки' },
] as const;

export type ServiceSpecializationCode = typeof SERVICE_SPECIALIZATION_OPTIONS[number]['code'];

const SERVICE_SPECIALIZATION_CODES = new Set<string>(
    SERVICE_SPECIALIZATION_OPTIONS.map(({ code }) => code)
);

const CATEGORY_SPECIALIZATION_CODES: Record<number, readonly ServiceSpecializationCode[]> = {
    10: ['crm_implementation', 'crm_configuration', 'crm_integration', 'one_c_configuration', 'one_c_integration'],
    11: [
        'custom_software_development',
        'website_development',
        'web_application_development',
        'mobile_application_development',
        'telegram_bot_development',
        'telegram_mini_app_development',
        'software_code_changes',
    ],
    30: ['loading_services', 'rigging_services', 'cargo_transportation', 'combined_move_service'],
    33: ['marketplace_operations', 'marketplace_growth'],
    35: ['graphic_design', 'packaging_design', 'marketplace_card_design'],
    66: ['loading_services', 'rigging_services', 'cargo_transportation', 'combined_move_service'],
    72: ['cargo_transportation', 'combined_move_service'],
};

export function serviceSpecializationCodesForCategory(categoryId?: number | null) {
    return categoryId ? CATEGORY_SPECIALIZATION_CODES[categoryId] || [] : [];
}

export function validServiceSpecializationCodes(value: unknown): value is ServiceSpecializationCode[] {
    return (
        Array.isArray(value) &&
        new Set(value).size === value.length &&
        value.every((code) => typeof code === 'string' && SERVICE_SPECIALIZATION_CODES.has(code))
    );
}

export function serviceSpecializationLabels(codes: readonly ServiceSpecializationCode[] = []) {
    const selected = new Set(codes);
    return SERVICE_SPECIALIZATION_OPTIONS.filter(({ code }) => selected.has(code)).map(({ label }) => label);
}

export default function ServiceSpecializationPicker({
    id,
    value,
    disabled,
    categoryId,
    onChange,
    showOptions = false,
}: {
    id: string;
    value: readonly ServiceSpecializationCode[];
    disabled?: boolean;
    categoryId: number;
    onChange: (codes: ServiceSpecializationCode[]) => void;
    showOptions?: boolean;
}) {
    const supportedCodes = serviceSpecializationCodesForCategory(categoryId);
    const availableCodes = new Set([...supportedCodes, ...value]);
    const options = SERVICE_SPECIALIZATION_OPTIONS.filter(({ code }) => availableCodes.has(code));
    const selected = SERVICE_SPECIALIZATION_OPTIONS.filter(({ code }) => value.includes(code));
    const hasOutsideCategory = value.some((code) => !supportedCodes.includes(code));
    if (showOptions) return (
        <Box component="fieldset" sx={{ m: 0, p: 0, border: 0, minWidth: 0 }}>
            <Typography component="legend" sx={{ fontSize: 15, fontWeight: 650, mb: 1 }}>Выберите специализации</Typography>
            <Stack direction="row" flexWrap="wrap" gap={1}>
                {options.map((option) => <Chip
                    key={option.code}
                    label={option.label}
                    clickable
                    disabled={disabled}
                    aria-pressed={value.includes(option.code)}
                    onClick={() => onChange((value.includes(option.code)
                        ? value.filter((code) => code !== option.code)
                        : [...value, option.code]).filter((code) => supportedCodes.includes(code)))}
                    variant={value.includes(option.code) ? 'filled' : 'outlined'}
                    sx={{ height: 'auto', minHeight: 36, borderRadius: '8px', maxWidth: '100%',
                        bgcolor: value.includes(option.code) ? 'var(--lm-accent-soft)' : 'var(--lm-surface)',
                        color: value.includes(option.code) ? 'var(--lm-accent-ink)' : 'var(--lm-text)',
                        border: '1px solid', borderColor: value.includes(option.code) ? 'var(--lm-accent)' : 'var(--lm-border)',
                        '& .MuiChip-label': { whiteSpace: 'normal', py: 0.75 } }}
                />)}
            </Stack>
            <Typography sx={{ mt: 1, fontSize: 13, color: 'var(--lm-muted)' }}>
                {hasOutsideCategory ? 'Сохранённые услуги относятся к другой категории. Снимите их и выберите подходящие.' : selected.length ? 'Поток будет искать выбранные услуги.' : 'Отметьте подходящие услуги. Без выбора поиск идёт по всей категории.'}
            </Typography>
        </Box>
    );
    return (
        <Autocomplete
            multiple
            disableCloseOnSelect
            options={options}
            value={selected}
            disabled={disabled}
            groupBy={(option) => option.group}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, selectedOption) => option.code === selectedOption.code}
            onChange={(_, selectedOptions) =>
                onChange(
                    selectedOptions
                        .map(({ code }) => code)
                        .filter((code) => supportedCodes.includes(code))
                )
            }
            renderTags={(options, getTagProps) =>
                options.map((option, index) => (
                    <Chip {...getTagProps({ index })} key={option.code} label={option.label} size="small" />
                ))
            }
            renderInput={(params) => (
                <TextField
                    {...params}
                    id={id}
                    label="Специализации"
                    placeholder={selected.length ? undefined : 'Выберите точные услуги'}
                    helperText={
                        hasOutsideCategory
                            ? 'Есть сохранённые специализации вне текущей категории. Выберите новый набор или очистите.'
                            : selected.length
                            ? 'Поток будет ограничен выбранными специализациями.'
                            : 'Без выбора действуют описание, категория, гео и исключения.'
                    }
                />
            )}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
        />
    );
}
